import React, { useCallback, useEffect, useState, type MouseEvent, useRef } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useQueryLoader } from 'react-relay';
import Badge from '@atlaskit/badge';
import SubtaskIcon from '@atlaskit/icon/core/subtasks';
import ChevronDownIconOld from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIconOld from '@atlaskit/icon/glyph/chevron-right';
import SubtaskIconOld from '@atlaskit/icon/glyph/subtask';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/chevron-right';
import { useIsReducedMotion } from '@atlaskit/motion';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { Box, xcss, type Space } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { useIsBulkOperationsInProgress } from '@atlassian/jira-bulk-operations-common/src/controllers/use-bulk-operations/index.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { isEpicHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import type { CardId } from '@atlassian/jira-platform-board-kit/src/common/types.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import VERSION_MENU_QUERY, {
	type versionMenuQuery,
} from '@atlassian/jira-relay/src/__generated__/versionMenuQuery.graphql';
import {
	CONTEXT_MENU_APPEARANCE,
	MEATBALL_MENU_APPEARANCE,
} from '@atlassian/jira-software-context-menu/src/common/constants.tsx';
import {
	useModalDialogActions,
	useReturnFocusTo,
} from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import {
	useShowEpics,
	useShowCompactMode,
	useCardFieldsSettings,
	useHasCardExtraFields,
} from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../../../feature-flags';
import { BACKLOG, ORIGINAL_ESTIMATE, STORY_POINTS } from '../../../../model/constants';
import type { ExtraField } from '../../../../model/extra-fields/types';
import type { IssueParent } from '../../../../model/issue-parent/types';
import type { CardPriority as CardPriorityType, EstimationType } from '../../../../model/types';
import { useBacklogDispatch, useBacklogSelector } from '../../../../state';
import { setHasViewedPulseRequest } from '../../../../state/actions/whiteboards-entrypoint-experiment';
import {
	makeGetDraggingCardIds,
	makeGetCardHierarchyLevel,
	makeOrderedVisibleCardIdsForCardList,
} from '../../../../state/selectors/cards';
import {
	getHasViewedPulse,
	getWhiteboardEligibility,
} from '../../../../state/selectors/whiteboards-entrypoint-experiment';
import { useIsCMPBacklog, useIsCMPKanbanBacklog } from '../../../../state/state-hooks/capabilities';
import { useShowCheckbox } from '../../../../state/state-hooks/use-show-checkbox';
import { WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS } from '../../../whiteboards-entrypoint-experiment/constants';
import { ScreenSize } from '../../hooks/common/constants';
import { useContainerSize } from '../../hooks/use-is-compact-view';
import { MENU_PLACEHOLDER_ID, CARD_CONTAINER_SELECTOR } from '../constants';
import { ContextMenu } from '../context-menu/view.tsx';
import messages from '../messages';
import {
	CardContainer,
	AssigneeContainer,
	ExtraFieldsContainer,
	MenuPlaceholder,
	DragHandleCardsAmount,
	StyledSpinner,
	OptionalFieldsContainer,
	CardGroupKey,
	CardGroupToggle,
	CheckboxContainer,
	SubtaskIconWrapper,
} from '../styled';
import AssigneeField from './assignee-field';
import CheckboxField from './checkbox-field';
import { ChildIssuesMetadata } from './child-issues-metadata';
import { CardColor } from './color-field';
import {
	CARDS_AMOUNT_BADGE_TESTID,
	CARD_CONTAINER_TESTID,
	CHECKBOX_TESTID,
	CHECKBOX_COMPONENT_SELECTOR,
} from './constants';
import { DueDate } from './due-date';
import { CardExtraField } from './extra-field';
import { FieldSeparator } from './extra-field/styled';
import { Flag } from './flag';
import InteractionLayer from './interaction-layer';
import { Key } from './key';
import OriginalEstimateField from './original-estimate-field';
import { Parent } from './parent';
import PriorityField from './priority-field';
import { StatusFieldWrapper } from './status-field/status-field-wrapper';
import StoryPointField from './story-point-field';
import { SummaryAndParents } from './summary-and-parents';
import { TypeIcon } from './type-icon';
import { VersionsWrapper } from './versions-field/version-field-wrapper';

export interface Props {
	cardId: number;
	isUserAtLeastMember: boolean;
	isFlagged: boolean;
	isSelected: boolean;
	isFocused: boolean;
	isDragging: boolean;
	/**
	 * True when this element is a pragmatic DnD compact native drag handle.
	 */
	isDragHandle?: boolean;
	/**
	 * When true render slower interactive elements. It'll be true on hover and focus.
	 */
	shouldRenderRichCard: boolean;
	canUserEditIssue: boolean;
	parents: IssueParent[];
	currentProjectId: number;
	projectIds: number[];
	projectAris: string[];
	storyPointFieldId: string | null;
	selectedCards: number[];
	numSelectedCards: number;
	highlightText: string | null;
	estimationType: EstimationType;
	timeTrackingOptions: TimeTrackingOptions;
	extraFields?: ExtraField[] | null;
	hasExtraFields: boolean;
	onClick: (
		withCmd: boolean,
		withShift: boolean,
		source: 'mouse' | 'keyboard',
		viaCheckbox?: boolean,
		externalAction?: ExternalAction,
	) => void;
	onFocus: () => void;
	onBlur: () => void;
	onToggleCardGroup: () => void;
	onContextMenu: () => void;
	cardKey: string;
	priority: CardPriorityType | null;
	summary: string;
	issueUrl: string;
	isDone: boolean;
	icon: string;
	numCompleteChildren: number;
	numTotalChildren: number;
	parentId: number | null;
	dueDate: string | null;
	shouldDisplayVersions: boolean;
	isAttachedToParent?: boolean;
	isSubtask: boolean;
	subtaskIds: CardId[] | undefined;
	parentKey: string | undefined | null;
	isCardGroupExpanded: boolean;
	onAddFlag: (event: MouseEvent) => void;
	shouldRenderInlineAddFlag: boolean;
	shouldRenderPriorityColumn: boolean;
	shouldRenderDueDateColumn: boolean;
	shouldRenderStoryPointColumn: boolean;
	shouldRenderEstimatesColumn: boolean;
}

const CardContents = ({
	cardId,
	cardKey,
	onClick,
	onFocus,
	onBlur,
	onContextMenu,
	highlightText,
	summary,
	parents,
	parentId,
	currentProjectId,
	projectIds,
	projectAris,
	issueUrl,
	icon,
	dueDate,
	estimationType,
	storyPointFieldId,
	timeTrackingOptions,
	extraFields,
	hasExtraFields,
	canUserEditIssue,
	isUserAtLeastMember,
	isDone,
	isFocused,
	isFlagged = false,
	isSelected = false,
	isDragging = false,
	isDragHandle = false,
	shouldRenderRichCard = true,
	selectedCards,
	numCompleteChildren,
	numTotalChildren,
	shouldDisplayVersions,
	isAttachedToParent = false,
	isSubtask = false,
	subtaskIds,
	parentKey,
	isCardGroupExpanded = false,
	onToggleCardGroup: onToggleCardGroupProp = noop,
	onAddFlag,
	shouldRenderInlineAddFlag,
	shouldRenderPriorityColumn,
	shouldRenderDueDateColumn,
	shouldRenderStoryPointColumn,
	shouldRenderEstimatesColumn,
}: Props) => {
	const prefersReducedMotion = useIsReducedMotion();
	const dispatch = useBacklogDispatch();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { size: backlogContainerSize } = useContainerSize();
	const isCompactView = backlogContainerSize === ScreenSize.EXTRA_SMALL;
	const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

	// TODO move to <SummaryAndParents /> during NON UIF clean up
	const [isSummaryFieldEditing, setIsSummaryFieldEditing] = useState(false);

	const [isActionMenuTriggerVisible] = useState(true);
	const cardHierarchyLevel = useBacklogSelector(makeGetCardHierarchyLevel);
	const isTopCardInBacklogContainer =
		useBacklogSelector((state) => makeOrderedVisibleCardIdsForCardList(state)(BACKLOG))[0] ===
		cardId;
	const isEpic = isEpicHierarchyLevel(cardHierarchyLevel(cardId));
	const isCompanyManaged = useIsCMPBacklog();
	const isCompanyManagedKanban = useIsCMPKanbanBacklog();

	const showEpics = useShowEpics();
	const showCompactMode = useShowCompactMode();
	const isCompactModeEnabled = showCompactMode;

	const returnFocusTo = useReturnFocusTo();
	const { setReturnFocusTo } = useModalDialogActions();
	const menuTriggerRef = useRef<HTMLButtonElement | null>(null);

	const isBulkOpsInProgress = useIsBulkOperationsInProgress();

	const shouldShowCheckbox = useShowCheckbox();

	const { formatMessage } = useIntl();
	const viewSettings = isViewSettingAsPanelExpEnabledWithNoExposure()
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useCardFieldsSettings()
		: {
				showIssueType: true,
				showIssueKey: true,
				showPriority: true,
				showAssignee: true,
				showEstimate: true,
				showDueDate: true,
				cardExtraFields: undefined,
			};

	const handleClick = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();

			if (e.defaultPrevented) {
				return;
			}

			const withCmd = e.ctrlKey || e.metaKey;
			const withShift = e.shiftKey;

			onClick(withCmd, withShift, 'mouse');
		},
		[onClick],
	);

	const onClickThrough = useCallback(
		(e: MouseEvent) => {
			const withCmd = e.ctrlKey || e.metaKey;
			const withShift = e.shiftKey;

			if (withCmd || withShift) {
				onFocus();
			}

			handleClick(e);
		},
		[onFocus, handleClick],
	);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			const withCmd = e.ctrlKey || e.metaKey;
			const withShift = e.shiftKey;

			if (e.key !== 'Enter' || isDragging) {
				return;
			}

			onClick(withCmd, withShift, 'keyboard');
		},
		[isDragging, onClick],
	);

	// TODO move to <SummaryAndParents /> on non UIF clean up
	const onSummaryEditStateChange = useCallback((isEditing: boolean) => {
		setIsSummaryFieldEditing(isEditing);
	}, []);

	const onRightClickOutside = useCallback((_e: Event) => {
		setIsActionMenuOpen(false);
	}, []);

	const [versionMenuQueryRef, loadVersionMenuQuery] =
		useQueryLoader<versionMenuQuery>(VERSION_MENU_QUERY);

	useEffect(() => {
		if (!versionMenuQueryRef && loadVersionMenuQuery != null && shouldRenderRichCard) {
			loadVersionMenuQuery({ jiraProjectIds: projectAris, first: 500, after: null });
		}
	}, [versionMenuQueryRef, loadVersionMenuQuery, shouldRenderRichCard, projectAris]);

	const renderCardColor = () => <CardColor id={cardId} />;

	const handleMenuOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen && menuTriggerRef?.current) {
				setReturnFocusTo(menuTriggerRef);
			}
			setIsActionMenuOpen(isOpen);
		},
		[setReturnFocusTo],
	);

	const handlePulseMenuOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen) {
				dispatch(setHasViewedPulseRequest());
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'opened',
						actionSubject: 'contextMenu',
					}),
					'contextMenuWithWhiteboardPulse',
				);
			}
			handleMenuOpenChange(isOpen);
		},
		[handleMenuOpenChange, dispatch, createAnalyticsEvent],
	);

	// for jsw_sprint_planning_whiteboards experiment
	const { cohort, flow } = useBacklogSelector(getWhiteboardEligibility);
	const hasViewedPulse = useBacklogSelector(getHasViewedPulse);
	const isPulseActive =
		isTopCardInBacklogContainer &&
		flow === 'crossFlow' &&
		cohort === WHITEBOARD_ENTRYPOINT_EXPERIMENT_VARIANTS.CONTEXT_MENU &&
		!hasViewedPulse;

	const getMenuComponent = () =>
		isPulseActive ? (
			<SpotlightPulse radius={3} pulse={!prefersReducedMotion}>
				<ContextMenu
					appearance={MEATBALL_MENU_APPEARANCE}
					onOpenChange={handlePulseMenuOpenChange}
					issueId={cardId}
					issueKey={cardKey}
					summary={summary}
					selectedCardIds={selectedCards}
					versionMenuQueryRef={versionMenuQueryRef}
					triggerRef={menuTriggerRef}
				/>
			</SpotlightPulse>
		) : (
			<ContextMenu
				appearance={MEATBALL_MENU_APPEARANCE}
				onOpenChange={handleMenuOpenChange}
				issueId={cardId}
				issueKey={cardKey}
				summary={summary}
				selectedCardIds={selectedCards}
				versionMenuQueryRef={versionMenuQueryRef}
				triggerRef={menuTriggerRef}
			/>
		);

	const renderMenu = () => {
		const shouldRenderMenu =
			(isActionMenuOpen || shouldRenderRichCard) && isActionMenuTriggerVisible;

		const triggerIsFocused =
			returnFocusTo?.current &&
			menuTriggerRef.current &&
			returnFocusTo?.current === menuTriggerRef.current;
		const isMenuOpen = isActionMenuOpen || Boolean(triggerIsFocused);

		return (
			<MenuPlaceholder
				isPulseActive={isPulseActive}
				isMenuOpen={isMenuOpen}
				data-test-id="software-backlog.card-list.card.card-contents.context-menu"
				data-testid="software-backlog.card-list.card.card-contents.context-menu"
				data-component-selector={MENU_PLACEHOLDER_ID}
			>
				{shouldRenderMenu || isPulseActive ? getMenuComponent() : null}
			</MenuPlaceholder>
		);
	};

	const renderInteractionLayer = () => (
		<InteractionLayer
			isFocused={isFocused}
			cardKey={cardKey}
			summary={summary}
			onFocus={onFocus}
			onBlur={onBlur}
			onClick={handleClick}
			onKeyDown={onKeyDown}
		/>
	);

	const renderOriginalEstimateField = () => {
		const estimateField = (
			<OriginalEstimateField
				issueId={cardId}
				issueKey={cardKey}
				estimationType={estimationType}
				timeTrackingOptions={timeTrackingOptions}
				isUserAtLeastMember={isUserAtLeastMember}
				shouldRenderRichCard={shouldRenderRichCard}
				shouldRenderEstimatesColumn={shouldRenderEstimatesColumn}
			/>
		);

		return estimationType === ORIGINAL_ESTIMATE ? estimateField : null;
	};

	const renderStoryPointField = () => {
		const estimateField = (
			<StoryPointField
				issueId={cardId}
				issueKey={cardKey}
				storyPointFieldId={storyPointFieldId}
				estimationType={estimationType}
				isUserAtLeastMember={isUserAtLeastMember}
				shouldRenderRichCard={shouldRenderRichCard}
				shouldRenderStoryPointColumn={shouldRenderStoryPointColumn}
			/>
		);

		return storyPointFieldId || estimationType === STORY_POINTS ? estimateField : null;
	};

	const renderAssigneeField = () => (
		<AssigneeContainer>
			<AssigneeField
				isUserAtLeastMember={isUserAtLeastMember}
				shouldRenderRichCard={shouldRenderRichCard}
				issueKey={cardKey}
			/>
		</AssigneeContainer>
	);

	const parentKeyText =
		parentKey !== undefined
			? formatMessage(messages.parentKeyText, {
					parentKey,
				})
			: '';

	const renderParentKey = () =>
		parentKey !== undefined && isSubtask && !isAttachedToParent ? (
			<ToolTip content={parentKeyText} position="mouse">
				<CardGroupKey>{`${parentKeyText}`}</CardGroupKey>
			</ToolTip>
		) : null;
	const hasLinkedSubtasks = subtaskIds !== undefined && subtaskIds.length > 0;
	const numberOfLinkedSubtasks = hasLinkedSubtasks
		? formatMessage(messages.numberOfChildrenText, {
				numberOfChildren: subtaskIds.length,
			})
		: '';
	const shouldShowChevron = hasLinkedSubtasks && !isDragHandle;

	const onToggleCardGroup = (event: MouseEvent<HTMLElement>) => {
		event.preventDefault();
		onToggleCardGroupProp();
	};

	const isExpanded = isCardGroupExpanded;

	const getChevronIcon = () => {
		if (isExpanded) {
			return fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
				<ChevronDownIcon
					label=""
					LEGACY_fallbackIcon={ChevronDownIconOld}
					color={token('color.icon')}
				/>
			) : (
				<ChevronDownIconOld label="" />
			);
		}
		return fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
			<ChevronRightIcon
				label=""
				LEGACY_fallbackIcon={ChevronRightIconOld}
				color={token('color.icon')}
			/>
		) : (
			<ChevronRightIconOld label="" />
		);
	};

	const getAriaLabel = () => {
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return formatMessage(
				isExpanded
					? messages.cardGroupToggleExpandedAriaLabelIssueTermRefresh
					: messages.cardGroupToggleCollapsedAriaLabelIssueTermRefresh,
				{
					issueKey: cardKey,
					summary,
				},
			);
		}

		return formatMessage(
			isExpanded
				? messages.cardGroupToggleExpandedAriaLabel
				: messages.cardGroupToggleCollapsedAriaLabel,
			{
				issueKey: cardKey,
				summary,
			},
		);
	};

	const renderCardGroupToggle = () => {
		if (!shouldShowChevron) return null;

		return (
			<ToolTip
				content={formatMessage(
					isExpanded
						? messages.cardGroupToggleTooltipCollapse
						: messages.cardGroupToggleTooltipExpand,
				)}
			>
				<IconWrapper>
					<CardGroupToggle
						appearance="subtle"
						onClick={onToggleCardGroup}
						aria-label={getAriaLabel()}
						testId="software-backlog.card-list.card.card-contents.subtask"
						aria-expanded={isExpanded}
						iconBefore={getChevronIcon()}
					/>
				</IconWrapper>
			</ToolTip>
		);
	};

	const getCheckboxContainerMargin = (shouldAddMargin: boolean | undefined): Space => {
		if (shouldAddMargin && isAttachedToParent) {
			return 'space.400';
		}
		if (shouldAddMargin) {
			return 'space.250';
		}
		return 'space.0';
	};

	const renderCheckbox = () => (
		<Box
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			xcss={xcss({
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				marginRight: getCheckboxContainerMargin(isCompanyManaged && !shouldShowChevron),
			})}
		>
			<CheckboxContainer
				data-component-selector={CHECKBOX_COMPONENT_SELECTOR}
				data-testid={CHECKBOX_TESTID}
				shouldShowCheckbox={shouldShowCheckbox}
			>
				<CheckboxField
					isSelected={isSelected}
					onClick={onClick}
					isDisabled={isBulkOpsInProgress}
					shouldShowCheckbox={shouldShowCheckbox}
					label={formatMessage(messages.cardCheckboxLabel, {
						cardKey,
						summary,
					})}
				/>
			</CheckboxContainer>
		</Box>
	);

	const showCardExtraFieldsColumnBasedOnViewSettings =
		isViewSettingAsPanelExpEnabledWithNoExposure()
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useHasCardExtraFields()
			: true;

	const renderExtraFields = () =>
		hasExtraFields &&
		extraFields &&
		showCardExtraFieldsColumnBasedOnViewSettings && (
			<ExtraFieldsContainer
				isSmartCardEnabled={expValEquals(
					'jsw_perf_format-links-in-custom-fields',
					'board-and-backlog',
					true,
				)}
				isAttachedToParent={isAttachedToParent && isCompanyManaged}
				hideTypeIcon={!viewSettings.showIssueType}
			>
				{extraFields
					.filter((field) =>
						viewSettings.cardExtraFields?.[field.id] === undefined
							? true
							: viewSettings.cardExtraFields[field.id],
					)
					.map((field, index) => (
						<React.Fragment key={field.id}>
							<CardExtraField field={field} />
							{index < extraFields?.length - 1 && (
								<FieldSeparator aria-hidden="true" key={`${field.id}-separator`}>
									•
								</FieldSeparator>
							)}
						</React.Fragment>
					))}
			</ExtraFieldsContainer>
		);

	const renderCmpChildIssuesMetadata = () =>
		hasLinkedSubtasks ? (
			<SubtaskIconWrapper>
				<ToolTip content={numberOfLinkedSubtasks} position="mouse">
					{fg('tnk-1620-visual-refresh-software-backlog-icons') ? (
						<SubtaskIcon
							color={token('color.icon.subtle', colors.N300)}
							LEGACY_primaryColor={token('color.icon.subtle', colors.N300)}
							LEGACY_size="small"
							label="subtaskIcon"
						/>
					) : (
						<SubtaskIconOld
							primaryColor={token('color.icon.subtle', colors.N300)}
							size="small"
							label="subtaskIcon"
						/>
					)}
				</ToolTip>
			</SubtaskIconWrapper>
		) : null;

	useEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.addEventListener('contextmenu', onRightClickOutside);

		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.removeEventListener('contextmenu', onRightClickOutside);
		};
	}, [onRightClickOutside]);

	const getDraggingCardIds = useBacklogSelector(makeGetDraggingCardIds);

	const isVisualRefreshBeta = fg('jira_nav4_beta_drop_1') && isVisualRefreshEnabled();

	const isOptimistic = cardId < 0;

	if (isOptimistic) {
		return (
			<CardContainer
				lowerOpacity={false}
				isSelected={isSelected}
				isFlagged={isFlagged}
				isDraggable={canUserEditIssue}
				isDragHandle={isDragHandle}
				hasExtraFields={hasExtraFields}
				isCompact={isCompactModeEnabled}
				data-component-selector={CARD_CONTAINER_SELECTOR}
				isVisualRefreshBeta={isVisualRefreshBeta}
			>
				<TypeIcon icon={icon} />
				<SummaryAndParents
					cardId={cardId}
					cardKey={cardKey}
					summary={summary}
					highlightText={highlightText}
					isUserAtLeastMember={isUserAtLeastMember}
					shouldRenderRichCard={shouldRenderRichCard}
					isSummaryFieldEditing={isSummaryFieldEditing}
					onClickThrough={onClickThrough}
					onSummaryEditStateChange={onSummaryEditStateChange}
				/>
				{!isDragHandle && renderExtraFields()}
				<StyledSpinner>
					<Spinner size="small" />
				</StyledSpinner>
			</CardContainer>
		);
	}

	const numSelected = getDraggingCardIds(cardId).length;

	return (
		<>
			<ContextMenu
				appearance={CONTEXT_MENU_APPEARANCE}
				issueId={cardId}
				issueKey={cardKey}
				selectedCardIds={selectedCards}
				versionMenuQueryRef={versionMenuQueryRef}
			>
				<CardContainer
					data-testid={CARD_CONTAINER_TESTID}
					shouldShowChevron={shouldShowChevron}
					isCompanyManagedKanban={isCompanyManagedKanban}
					isCompact={isCompactModeEnabled}
					isAttachedToParent={isAttachedToParent}
					isSelected={!isDragHandle && isSelected}
					isFlagged={isFlagged}
					lowerOpacity={isDragging && !isDragHandle}
					isDragHandle={isDragHandle}
					isDraggable={canUserEditIssue}
					hasExtraFields={
						!isDragHandle && hasExtraFields && showCardExtraFieldsColumnBasedOnViewSettings
					}
					onContextMenu={onContextMenu}
					hideTypeIcon={!viewSettings.showIssueType}
					isAssigneeShown={viewSettings.showAssignee}
					data-component-selector={CARD_CONTAINER_SELECTOR}
					isVisualRefreshBeta={isVisualRefreshBeta}
				>
					{!isDragHandle ? renderInteractionLayer() : null}
					{!isDragHandle ? renderCheckbox() : null}
					{renderCardColor()}
					{renderCardGroupToggle()}
					{viewSettings.showIssueType && <TypeIcon icon={icon} />}
					{viewSettings.showIssueKey && (
						<Key
							isDone={isDone}
							cardKey={cardKey}
							issueUrl={issueUrl}
							summary={summary}
							highlightText={highlightText}
							onClick={onClick}
						/>
					)}
					<SummaryAndParents
						cardId={cardId}
						cardKey={cardKey}
						summary={summary}
						highlightText={highlightText}
						isUserAtLeastMember={isUserAtLeastMember}
						shouldRenderRichCard={shouldRenderRichCard}
						isSummaryFieldEditing={isSummaryFieldEditing}
						onClickThrough={onClickThrough}
						onSummaryEditStateChange={onSummaryEditStateChange}
					/>
					{renderParentKey()}
					<>
						<OptionalFieldsContainer>
							{!isDragHandle && (
								<>
									<ChildIssuesMetadata
										numTotalChildren={numTotalChildren}
										numCompleteChildren={numCompleteChildren}
										onClickThrough={onClickThrough}
									/>

									{renderCmpChildIssuesMetadata()}
									<VersionsWrapper
										shouldDisplayVersions={shouldDisplayVersions}
										backlogContainerSize={backlogContainerSize}
										cardId={cardId}
									/>

									{showEpics && (
										<Parent
											cardId={cardId}
											cardKey={cardKey}
											isUserAtLeastMember={isUserAtLeastMember}
											parents={parents}
											parentId={parentId}
											currentProjectId={currentProjectId}
											projectIds={projectIds}
											canUserEditIssue={canUserEditIssue}
											shouldRenderRichCard={shouldRenderRichCard}
											backlogContainerSize={backlogContainerSize}
											isCompactView={isCompactView}
											isSubtask={isSubtask}
											isEpic={isEpic}
											isCompanyManaged={isCompanyManaged}
										/>
									)}

									<StatusFieldWrapper
										isUserAtLeastMember={isUserAtLeastMember}
										shouldRenderRichCard={shouldRenderRichCard}
										cardKey={cardKey}
										cardId={cardId}
									/>
								</>
							)}

							{!isDragHandle && (
								<>
									{viewSettings.showDueDate && (
										<DueDate
											dueDate={dueDate}
											shouldRenderDueDateColumn={shouldRenderDueDateColumn}
											isDone={isDone}
										/>
									)}

									<Flag
										isFlagged={isFlagged}
										shouldRenderInlineAddFlag={shouldRenderInlineAddFlag}
										onAddFlag={onAddFlag}
									/>
									{viewSettings.showEstimate && renderOriginalEstimateField()}
									{viewSettings.showEstimate && renderStoryPointField()}
									{viewSettings.showPriority && (
										<PriorityField
											isUserAtLeastMember={isUserAtLeastMember}
											issueKey={cardKey}
											shouldRenderRichCard={shouldRenderRichCard}
											shouldRenderColumn={shouldRenderPriorityColumn}
										/>
									)}
								</>
							)}
						</OptionalFieldsContainer>
						{!isDragHandle && viewSettings.showAssignee && renderAssigneeField()}
						{!isDragHandle && renderMenu()}
					</>
					{!isDragHandle && renderExtraFields()}
				</CardContainer>
			</ContextMenu>
			{isDragHandle && numSelected > 1 && (
				<DragHandleCardsAmount>
					<Badge appearance="primary" testId={CARDS_AMOUNT_BADGE_TESTID}>
						{numSelected}
					</Badge>
				</DragHandleCardsAmount>
			)}
		</>
	);
};

export default CardContents;

// Chevron icon wrapper, keep it consistent with src/packages/software/backlog/src/view/card-list/card-lite-extended/index.tsx
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	gridColumn: 'chevron / span 1',
	gridRow: 'card-detail / span 1',
	display: 'flex',
	justifyContent: 'center',
});
